import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaRegClock, FaStar, FaStarHalfAlt, FaRegStar, FaClock } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { IoArrowBackOutline } from "react-icons/io5";
import "../styles/MoviePage.css";
import { saveBookLater, removeBookLater, getBookLater, getAllHallSchedules } from "../config/firebaseService";
import { AuthContext } from "../components/AuthContext";

// API configuration for fetching movie data
const API_KEY = "814254e9d1fb4859da3f4798b86b6f49";
const BASE_URL = "https://api.themoviedb.org/3";

const MoviePage = () => {
    // Extract movie ID from the URL parameters
  const { id } = useParams();
  const navigate = useNavigate();

  // State variables for storing movie details, cast, and showtimes
  const [movie, setMovie] = useState(null);
  const [cast, setCast] = useState([]);
 const [showtimes, setShowtimes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Flags for different movie categories
  const [isNowPlaying, setIsNowPlaying] = useState(false);
  const [isTopRated, setIsTopRated] = useState(false);
  const [isUpcoming, setIsUpcoming] = useState(false);

  // Track Book Later items (Firestore-only)
  const [bookLaterIds, setBookLaterIds] = useState(new Set());
  const { user } = useContext(AuthContext);
  const numericId = parseInt(id);

  const isFavorite = bookLaterIds.has(numericId);

  // Load current user's Book Later list to know if this movie is saved
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!user?.id) {
        if (active) setBookLaterIds(new Set());
        return;
      }
      try {
        const items = await getBookLater(user.id);
        if (!active) return;
        const ids = new Set(items.map((m) => parseInt(m.id)));
        setBookLaterIds(ids);
      } catch (e) {
        console.warn("Failed to load Book Later:", e);
      }
    };
    load();
    return () => { active = false; };
  }, [user, id]);


  useEffect(() => {
    const fetchMovie = async () => {
      try {
        // Fetch movie details
        const response = await fetch(`${BASE_URL}/movie/${id}?api_key=${API_KEY}&language=en-US`);
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        const data = await response.json();
        setMovie(data);

        // Check if the movie is currently playing using local server's dataset
        const nowPlayingResponse = await fetch(`/api/movies/now_playing?page=1`);
        const nowPlayingData = await nowPlayingResponse.json();
        const np = Array.isArray(nowPlayingData) ? nowPlayingData : (nowPlayingData?.results || []);
        const isPlaying = np.some(m => parseInt(m.id) === data.id);
        setIsNowPlaying(isPlaying);

        // Fetch showtimes from Firestore schedules if the movie is playing now
        if (isPlaying) {
          try {
            const halls = await getAllHallSchedules();
            // Flatten all hall schedules and filter by this movie id
            const entries = [];
            for (const hallDoc of halls) {
              const hallName = hallDoc.hall || hallDoc.id;
              const sch = Array.isArray(hallDoc.schedule) ? hallDoc.schedule : [];
              for (const s of sch) {
                const sId = parseInt(String(s.movieId));
                if (sId === data.id) {
                  entries.push({
                    showtimeId: `${hallName}-${s.showtime}`,
                    hall: hallName,
                    showtime: s.time || (s.showtime?.split(" ")[1] ?? ""),
                    date: s.date || (s.showtime?.split(" ")[0] ?? ""),
                  });
                }
              }
            }
            setShowtimes(entries);
          } catch (e) {
            console.warn("Failed to load Firestore showtimes:", e);
            setShowtimes([]);
          }
        }

        // Check if the movie is top-rated
        const topRatedResponse = await fetch(`${BASE_URL}/movie/top_rated?api_key=${API_KEY}&language=en-US`);
        const topRatedData = await topRatedResponse.json();
        setIsTopRated(topRatedData.results.some(m => m.id === data.id));

        // Check if the movie is upcoming (but not currently playing)
        if (!isPlaying) {
          const upcomingResponse = await fetch(`${BASE_URL}/movie/upcoming?api_key=${API_KEY}&language=en-US`);
          const upcomingData = await upcomingResponse.json();
          setIsUpcoming(upcomingData.results.some(m => m.id === data.id));
        } else {
          setIsUpcoming(false);
        }
      } catch (error) {
        console.error("Error fetching movie details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch movie cast details
    const fetchCast = async () => {
      try {
        const response = await fetch(`${BASE_URL}/movie/${id}/credits?api_key=${API_KEY}&language=en-US`);
        if (!response.ok) {
          throw new Error(`Error fetching cast: ${response.status}`);
        }
        const data = await response.json();
        setCast(data.cast.slice(0, 6)); // Limit to 6 cast members
      } catch (error) {
        console.error("Error fetching cast details:", error);
      }
    };

    fetchMovie();
    fetchCast();
  }, [id]);

  // Function to add or remove a movie from favorites
  const toggleFavorite = async () => {
    if (!user?.id) {
      // Require login for Book Later
      navigate("/login");
      return;
    }
    const next = new Set(bookLaterIds);
    if (isFavorite) {
      try {
        await removeBookLater(user.id, numericId);
        next.delete(numericId);
      } catch (e) {
        console.warn("Failed to remove from Firestore:", e);
      }
    } else {
      try {
        await saveBookLater(user.id, movie);
        next.add(numericId);
      } catch (e) {
        console.warn("Failed to save to Firestore:", e);
      }
    }
    setBookLaterIds(next);
  };

  // Helper: determine if a given show (YYYY-MM-DD and HH:mm or 12h) is in the past
  const isShowPast = (dateStr, timeStr) => {
    if (!timeStr) return false;
    let t = String(timeStr).trim();
    // Normalize 12-hour formats like 8:00 PM to 24-hour
    const ampm = t.match(/^(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])$/);
    if (ampm) {
      let hh = parseInt(ampm[1], 10);
      const mm = ampm[2] ? ampm[2] : "00";
      const suffix = ampm[3].toLowerCase();
      if (suffix === "pm" && hh !== 12) hh += 12;
      if (suffix === "am" && hh === 12) hh = 0;
      t = `${String(hh).padStart(2, "0")}:${mm}`;
    } else {
      // Ensure HH:mm
      const m = t.match(/^(\d{1,2}):(\d{2})/);
      if (m) t = `${m[1].padStart(2, "0")}:${m[2]}`;
    }
    let d = dateStr;
    if (!d) {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      d = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    }
    const dt = new Date(`${d}T${t}:00`);
    if (Number.isNaN(dt.getTime())) return false;
    return dt.getTime() < Date.now();
  };

  // Normalize a time string (e.g., "8:00 PM") to HH:mm (e.g., "20:00") for <time dateTime>
  const toIsoTime = (timeStr) => {
    if (!timeStr) return "";
    let t = String(timeStr).trim();
    const ampm = t.match(/^(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])$/);
    if (ampm) {
      let hh = parseInt(ampm[1], 10);
      const mm = ampm[2] ? ampm[2] : "00";
      const suffix = ampm[3].toLowerCase();
      if (suffix === "pm" && hh !== 12) hh += 12;
      if (suffix === "am" && hh === 12) hh = 0;
      return `${String(hh).padStart(2, "0")}:${mm}`;
    }
    const m = t.match(/^(\d{1,2}):(\d{2})/);
    if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
    return t;
  };

  // Function to render star ratings
  const renderStars = (rating) => {
    const items = [];
    const fullStars = Math.floor(rating / 2);
    const halfStar = rating % 2 >= 1;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

    for (let i = 0; i < fullStars; i++) items.push(
      <li key={`full-${i}`} aria-hidden="true"><FaStar className="star" /></li>
    );
    if (halfStar) items.push(
      <li key="half" aria-hidden="true"><FaStarHalfAlt className="star" /></li>
    );
    for (let i = 0; i < emptyStars; i++) items.push(
      <li key={`empty-${i}`} aria-hidden="true"><FaRegStar className="star" /></li>
    );

    return items;
  };

  if (isLoading) return <p>Loading movie details...</p>;
  if (!movie) return <p>Movie not found.</p>;

  return (
    <main className="movie-page" style={{ backgroundImage: `url(https://image.tmdb.org/t/p/original${movie.backdrop_path})` }}>
      {/* Overlay handled via CSS pseudo-element */}
      <nav aria-label="Back navigation">
        <button className="back-button" onClick={() => navigate(-1)} aria-label="Go back">
          <IoArrowBackOutline />
        </button>
      </nav>

      <section className="movie-container" aria-labelledby="movie-title">
        <figure className="movie-poster-container">
          <img src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`} alt={movie.title} className="movie-poster-two" />
         {/* Mostrar "Watch Later" solo si la película está en Now Playing */}
          {isNowPlaying && (
            <button
              className={`watch-later ${isFavorite ? "watch-later-active" : ""}`}
              onClick={toggleFavorite}
              style={{ marginTop: "15px" }}
            >
              {isFavorite ? <IoMdClose /> : <FaRegClock />} {isFavorite ? "Remove from Book Later" : "Book Later"}
            </button>
          )}
        </figure>

        <section className="movie-info-box">
          <h1 className="movie-title" id="movie-title">{movie.title}</h1>
          <p><strong>Genre:</strong> {movie.genres?.map((g) => g.name).join(", ")}</p>
          <p>
            <strong>Duration:</strong>{" "}
            <time dateTime={`PT${movie.runtime}M`}>{movie.runtime} minutes</time>
          </p>
          <p><strong>Age Rating:</strong> {movie.adult ? "18+" : "All ages"}</p>
          <details>
            <summary><strong>Overview</strong></summary>
            <p>{movie.overview}</p>
          </details>
          <section aria-labelledby="cast-heading">
            <h2 id="cast-heading">Cast</h2>
            <ul>
              {cast.map((actor) => (
                <li key={actor.id}>{actor.name}</li>
              ))}
            </ul>
          </section>
        </section>
      </section>

{/* Mostrar solo si es Now Playing */}
{isNowPlaying && (
  <section className="showtimes-box" aria-labelledby="available-showtimes-heading">
    <h3 id="available-showtimes-heading">Available Showtimes</h3>
    {showtimes.length > 0 ? (
      <ul className="showtimes-list">
        {showtimes.map((show, index) => {
          const past = isShowPast(show.date, show.showtime);
          const isoTime = toIsoTime(show.showtime);
          return (
            <li key={index}>
              <button
                className="showtime-button"
                disabled={past}
                title={past ? "This show has ended" : "Book this showtime"}
                aria-label={past ? `Showtime ${show.showtime} ended` : `Book showtime ${show.showtime}`}
                onClick={() => {
                  if (past) return;
                  navigate(`/seats/${show.showtimeId}`,{
                    state: {
                      movieSummary: {
                        id: movie.id,
                        title: movie.title,
                        poster_path: movie.poster_path,
                        runtime: movie.runtime,
                        genres: movie.genres,
                        hall: show.hall,
                        date: show.date,
                        showtime: show.showtime,
                      },
                    },
                  });
                }}
              >
                <strong>🎥 {show.hall}</strong> – <time dateTime={`${show.date}T${isoTime}:00`}>{show.showtime}</time>
              </button>
            </li>
          );
        })}
      </ul>
    ) : (
      <p>No showtimes available at the moment.</p>
    )}
  </section>
)}


      {/* Show just if it is Top Rated */}
      {isTopRated && (
        <article className="top-rated-box">
          <h3>Movie Rating: {movie.vote_average.toFixed(1)}</h3>
          <ul className="stars" aria-label={`Rating: ${movie.vote_average.toFixed(1)} out of 10`}>
            {renderStars(movie.vote_average)}
          </ul>
          <p>
            <strong>Score:</strong>{" "}
            <progress max="10" value={Number(movie.vote_average.toFixed(1))}>{movie.vote_average.toFixed(1)}</progress>
          </p>
          <p className="info-text">
            This movie is no longer playing in theaters, but it might return in the future due to its high popularity.
          </p>
        </article>
      )}

      {/* Show just if it is Upcoming */}
      {isUpcoming && (
        <article className="upcoming-box">
          <h3><FaClock className="upcoming-icon" /> Not now, but coming soon...</h3>
          <p className="info-text">
            This highly anticipated movie will be available soon. Stay tuned for its release date!
          </p>
        </article>
      )}
    </main>
  );
};

export default MoviePage;
